import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/site/Reveal";
import { money } from "@/lib/api";

function Toggle({ mode, setMode }) {
  return (
    <div className="inline-flex rounded-full border border-white/15 bg-black/60 p-1 backdrop-blur" data-testid="pricing-toggle">
      {[["peak", "Peak"], ["offpeak", "Off-Peak"]].map(([v, l]) => (
        <button key={v} onClick={() => setMode(v)} data-testid={`pricing-toggle-${v}`} aria-pressed={mode === v}
          className={`rounded-full px-6 py-2 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-200 ${mode === v ? "bg-[var(--gold)] text-black" : "text-white/60 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
}

function Price({ value, suffix, testid }) {
  return (
    <div className="flex items-end gap-1 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span key={value} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -24, opacity: 0 }} transition={{ duration: 0.25 }}
          className="h-display text-6xl text-white" data-testid={testid}>{money(value)}</motion.span>
      </AnimatePresence>
      {suffix && <span className="mb-2 text-sm text-white/50">{suffix}</span>}
    </div>
  );
}

export function PlanCard({ plan, mode, delay = 0 }) {
  const { openJoin } = useSite();
  const off = mode === "offpeak" && plan.offpeak_price != null;
  const price = off ? plan.offpeak_price : plan.peak_price;
  const start = off ? plan.start_offpeak : plan.start_peak;
  const featured = plan.badge === "Best Seller";
  return (
    <Reveal delay={delay} className="h-full">
      <div className={`glass relative flex h-full flex-col rounded-md p-6 shadow-2xl transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[var(--gold)]/60 ${featured ? "!border-[var(--gold)]/70" : ""}`} data-testid={`plan-card-${plan.id}`}>
        {plan.badge && (
          <span className={`absolute -top-3 left-6 rounded-sm px-3 py-1 font-mono-ui text-[11px] font-bold uppercase tracking-wider ${featured ? "bg-[var(--gold)] text-black" : "border border-[var(--gold)] bg-black text-[var(--gold)]"}`} data-testid={`plan-badge-${plan.id}`}>{plan.badge}</span>
        )}
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.2em] text-white/45">{plan.subtitle}</p>
        <h3 className="h-display mt-1 text-3xl text-white">{plan.name}</h3>
        <div className="mt-4"><Price value={price} suffix={plan.suffix} testid={`plan-price-${plan.id}`} /></div>
        {mode === "offpeak" && plan.offpeak_price == null && plan.group === "core" && (
          <p className="mt-1 font-mono-ui text-[11px] uppercase tracking-wider text-[var(--gold)]">Includes unlimited peak access</p>
        )}
        {start != null && <p className="mt-2 text-sm text-white/70" data-testid={`plan-start-${plan.id}`}>{plan.start_label}: <span className="font-bold text-[var(--gold)]">{money(start)}</span></p>}
        <p className="mt-4 text-sm leading-relaxed text-white/60">{plan.description}</p>
        {plan.features?.length > 0 && (
          <ul className="mt-4 space-y-2">
            {plan.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-white/80"><Check className="h-4 w-4 text-[var(--gold)]" />{f}</li>)}
          </ul>
        )}
        {plan.note && <p className="mt-4 text-xs italic text-white/45">{plan.note}</p>}
        <div className="mt-auto pt-6">
          <button onClick={() => openJoin({ type: plan.id === "guest-pass" ? "guest_pass" : "membership", plan: plan.name })}
            className={featured ? "btn-gold w-full" : "btn-ghost w-full"} data-testid={`plan-join-btn-${plan.id}`}>
            {plan.id === "guest-pass" ? "Get a Guest Pass" : "Join Now"}
          </button>
        </div>
      </div>
    </Reveal>
  );
}

export function PricingSection() {
  const { content } = useSite();
  const [mode, setMode] = useState("peak");
  const core = content.plans.filter((p) => p.group === "core");
  const extra = content.plans.filter((p) => p.group !== "core");
  const s = content.settings;
  return (
    <section className="grain relative overflow-hidden" data-testid="pricing-section">
      <img src="/images/barbell.webp" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <p className="eyebrow">Every plan works at both gyms</p>
            <h2 className="h-display text-4xl text-white sm:text-5xl lg:text-6xl">Pick your plan</h2>
            <p className="max-w-xl text-sm text-white/60 sm:text-base" data-testid="pricing-mode-description">
              <span className="font-bold text-white">{mode === "peak" ? "Peak:" : "Off-Peak:"}</span> {mode === "peak" ? s.peak_definition : s.offpeak_definition}
            </p>
          </div>
          <Toggle mode={mode} setMode={setMode} />
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {core.map((p, i) => <PlanCard key={p.id} plan={p} mode={mode} delay={i * 0.08} />)}
        </div>
        <p className="eyebrow mt-16">More ways to train</p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {extra.map((p, i) => <PlanCard key={p.id} plan={p} mode={mode} delay={i * 0.08} />)}
        </div>
        <p className="mt-10 text-xs text-white/45" data-testid="pricing-footnote">{s.pricing_footnote}</p>
      </div>
    </section>
  );
}
